/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions,jsx-a11y/anchor-is-valid */
import React from 'react';
import { connect } from 'react-redux';
import {
  InputLabel,
  SelectProps,
  Select,
  MenuItem,
  ListSubheader,
  Divider,
} from '@mui/material';
import { v4 } from 'uuid';
import {
  Container,
  SelectedTeamsContainer,
  ParamsContainer,
  FormContainer,
  FilterContainer,
} from './styles';
import toast from '../../../utils/toast';
import { IAppState } from '../../../state/reducers';
import { selectExchangeQualifiersOptions, ILookupOption } from '../../../state/lookups';
import {
  DownloadFilter,
  PushToCloudFilter,
  toggleFilterByQualifier,
  toggleFilterByDownload,
  toggleFilterByFileType,
  selectSelectedTeams,
  queryTeams,
  loadVendorSports,
} from '../../../state/exchange/searchParams';
import { resetActiveAsset } from '../../../state/exchange/activeAsset';
import {
  IProps,
  IStateProps,
  IDispatchProps,
  ISelectOption,
} from './interfaces';
import SportSelection from './SportSelection/component';
import SeasonSelection from './SeasonSelection/component';
import DateSelection from './DateSelection/component';
import TeamsSelection from './TeamsSelection/component';
import SelectedTeams from './SelectedTeams/component';
import { selectIsUserExchangeVendor, selectIsUserVaultSuperAdmin } from '../../../state/user/userOrgRole';
import { getLocale } from '../../../state/localization';
import { LocalizationLabels } from '../../../enums';

const downloadFilterOptions: Array<ISelectOption> = [
  { value: DownloadFilter.Downloaded, label: 'Downloaded' },
  { value: DownloadFilter.NotDownloaded, label: 'Not Downloaded' },
];

const pushToCloudFilterOptions: Array<ISelectOption> = [
  { value: PushToCloudFilter.PushedToCloud, label: 'Pushed To Cloud' },
  { value: PushToCloudFilter.NotPushedToCloud, label: 'Not Pushed To Cloud' },
];

const selectProps: SelectProps = {
  MenuProps: {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center',
    },
  },
};

class ExchangeSearchParams extends React.Component<IProps> {
  componentDidMount() {
    const { onLoad, onloadVendorSports } = this.props;
    onLoad();
    onloadVendorSports();
  }

  handleTogglePhaseGroup(parent: ILookupOption) {
    const {
      toggleQualifier,
      resetActiveAssetConnect,
      qualifierFilter,
    } = this.props;
    const children: Array<string> = parent.children.map(({ value }) => value);
    let options: Array<string>;
    // TODO: replace includes with set/intersect if necessary for performance
    if (children.every((value) => qualifierFilter.includes(value))) {
      // note: in the event all children selected, de-select all
      options = qualifierFilter.filter((value: string) => !children.includes(value));
    } else {
      // note: in the event all children are not selected, select missing
      options = [...qualifierFilter, ...children.filter((value: string) => !qualifierFilter.includes(value))];
    }
    toggleQualifier(options);
    resetActiveAssetConnect();
  }

  handlePhaseChange(e: any) {
    const { toggleQualifier, resetActiveAssetConnect } = this.props;
    const qualifierFilter = e.target.value;
    if (qualifierFilter.includes(undefined)) {
      // note: assume parent clicked
      return;
    }
    toggleQualifier(qualifierFilter);
    resetActiveAssetConnect();
  }

  handleDownloadChange(e: any) {
    const { toggleDownload, resetActiveAssetConnect } = this.props;
    const selectedFilter = e.target.value;
    toggleDownload(selectedFilter);
    resetActiveAssetConnect();
  }

  handleFileTypeChange(e: any) {
    const { toggleFileType, resetActiveAssetConnect } = this.props;
    const selectedFileTypes = e.target.value;
    toggleFileType(selectedFileTypes);
    resetActiveAssetConnect();
  }

  onFileTypeClick(e: any) {
    const { fileTypes, isVaultSuperAdmin } = this.props;
    if (!fileTypes || !fileTypes.length) {
      if (!isVaultSuperAdmin) {
        toast('Try searching for games before filtering by file type', {
          type: toast.TYPE.WARNING,
        });
      }
    }
  }

  getFilterOptions(): Array<ISelectOption> {
    const { subscription: { isPushToCloud }, downloadFilter } = this.props;
    if (isPushToCloud) {
      // Check if content delivery was switched while filter was active
      if (downloadFilter === downloadFilterOptions[0].value || downloadFilter === downloadFilterOptions[1].value) {
        this.handleDownloadChange({ target: { value: null } });
      }
      return pushToCloudFilterOptions;
    }
    // Check if content delivery was switched while filter was active
    if (downloadFilter === pushToCloudFilterOptions[0].value || downloadFilter === pushToCloudFilterOptions[1].value) {
      this.handleDownloadChange({ target: { value: null } });
    }
    return downloadFilterOptions;
  }

  render() {
    const {
      qualifierFilter,
      phaseOptions,
      downloadFilter,
      fileTypes,
      fileTypeFilter,
      isVendor,
      vendorSports,
      phaseLabel,
    } = this.props;

    return (
      <Container>
        <ParamsContainer
          data-auto="SearchParms"
          className="SearchParms"
        >
          {isVendor && vendorSports.length > 0 && <SportSelection />}
          <SeasonSelection />
          <TeamsSelection />
          <DateSelection />
        </ParamsContainer>
        <SelectedTeamsContainer>
          <SelectedTeams />
          <FormContainer row>
            <FilterContainer variant="standard">
              <InputLabel id="downloadLabel">Status</InputLabel>
              <Select
                {...selectProps}
                labelId="downloadLabel"
                onChange={(e) => this.handleDownloadChange(e)}
                value={downloadFilter ?? ''}
                label="Status"
              >
                <MenuItem value="" key={v4()}>All</MenuItem>
                {this.getFilterOptions().map((option) => (
                  <MenuItem value={option.value} key={v4()}>{option.label}</MenuItem>
                ))}
              </Select>
            </FilterContainer>
            <FilterContainer variant="standard">
              <InputLabel id="phaseLabel">{phaseLabel}</InputLabel>
              <Select
                {...selectProps}
                labelId="phaseLabel"
                onChange={(e) => this.handlePhaseChange(e)}
                value={qualifierFilter}
                multiple
                data-auto="PhasePickList"
                label={phaseLabel}
              >
                {phaseOptions.map(({ value, label, children }) => {
                  if (children.length > 0) {
                    const items = children.map((child) => <MenuItem style={{ paddingLeft: '24px' }} value={child.value} key={v4()}>{child.label}</MenuItem>);
                    // note: need to use anchor tag for click event
                    items.unshift((
                      <ListSubheader>
                        <a
                          onClick={(e) => {
                            this.handleTogglePhaseGroup({ value, label, children } as ILookupOption);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {label}
                        </a>
                      </ListSubheader>
                    ));
                    return items;
                  }
                  return <MenuItem value={value} key={v4()}>{label}</MenuItem>;
                })}
              </Select>
            </FilterContainer>
            <FilterContainer variant="standard">
              <InputLabel id="fileTypeLabel">File Type</InputLabel>
              <Select
                {...selectProps}
                labelId="fileTypeLabel"
                onChange={(e) => this.handleFileTypeChange(e)}
                onClick={(e) => this.onFileTypeClick(e)}
                value={fileTypeFilter || []}
                disabled={!fileTypes.length}
                multiple
                data-auto="FileTypePickList"
                label="File Type"
              >
                {fileTypes.map((fileType) => (
                  <MenuItem value={fileType} key={v4()}>{fileType}</MenuItem>
                ))}
              </Select>
            </FilterContainer>
          </FormContainer>
        </SelectedTeamsContainer>
      </Container>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  fileTypes: state.exchange.searchResults.fileTypes,
  isFilterByQualifier: state.exchange.searchParams.isFilterByQualifier,
  qualifierFilter: state.exchange.searchParams.qualifierFilter,
  isFilterByDownload: state.exchange.searchParams.isFilterByDownload,
  downloadFilter: state.exchange.searchParams.downloadFilter,
  isFilterByFileType: state.exchange.searchParams.isFilterByFileType,
  fileTypeFilter: state.exchange.searchParams.fileTypeFilter,
  selectedTeams: selectSelectedTeams(state),
  phaseOptions: selectExchangeQualifiersOptions(state),
  subscription: state.exchange.contentPreferences.subscription,
  isVendor: selectIsUserExchangeVendor(state),
  vendorSports: state.exchange.searchParams.vendorSports,
  phaseLabel: getLocale(state, LocalizationLabels.ExchangePhase),
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
});

const mapDispatchToProps: IDispatchProps = {
  onLoad: queryTeams,
  onloadVendorSports: loadVendorSports,
  toggleQualifier: toggleFilterByQualifier,
  toggleDownload: toggleFilterByDownload,
  toggleFileType: toggleFilterByFileType,
  resetActiveAssetConnect: resetActiveAsset,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ExchangeSearchParams);
